import {
  SNAKE_BONUS_EVERY,
  SNAKE_BONUS_GROWTH,
  SNAKE_BONUS_SIZE,
  SNAKE_BONUS_STEPS,
  SNAKE_BONUS_TICK,
  SNAKE_GRID,
  SNAKE_GROWTH,
  SNAKE_INITIAL_LENGTH,
  SNAKE_SPEEDS,
  type SnakeSpeed,
} from "@bing/shared";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "../../components/Button";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EndCardShell } from "../../components/EndCardShell";
import { GameIcon } from "../../components/GameIcon";
import { faDigits } from "../../i18n/digits";
import { fa } from "../../i18n/fa";
import { lifecycle } from "../lifecycle";
import type { SnakeBestScore } from "./best";
import { browserClock, SnakeController, type SnakePhase } from "./controller";
import { lcdColour, SnakePainter, type SnakeLook } from "./draw";
import { SnakeGame, type SnakeDir, type SnakeEnd } from "./engine";
import { layoutSnake, SNAKE_BAND_PX, type SnakeLayout, type SnakeRect } from "./layout";

/**
 * «مار» — THE PLAY SCREEN: the header band (exit · title · score box), the board, the «+» pad, the
 * 3-2-1 and the end card — the game over, or the win when the snake fills the board. It only WIRES
 * the game: the rules are `engine.ts`, the clock is `controller.ts`, the geometry `layout.ts`, the
 * paint `draw.ts`, the record `best.ts`.
 *
 * Every best here is THIS SPEED's (the operator, 2026-11-02: a separate best for each speed): the
 * header's, the end card's and the one «رکورد جدید!» is measured against.
 *
 * ⚠️ REACT RE-RENDERS ON A PHASE, A COUNTDOWN NUMBER OR A SCORE — NEVER ON A STEP. A step paints its
 * few cells on the canvas and writes the bonus's countdown straight into its text node.
 *
 * ⚠️ NOTHING RUNS WHILE THE APP IS HIDDEN: the document going hidden (at once) and the app's fused
 * activity signal (`lifecycle`: Telegram's activated/deactivated, pagehide, freeze) both pause the
 * game, and every timer goes with the pause. The listeners exist only while this screen does.
 */

const DIRS: readonly SnakeDir[] = ["up", "left", "right", "down"];

/**
 * A desktop's keys: the arrows and W A S D, read by `code` — the key's PLACE, not its letter — so a
 * Persian layout (ص ش س ی) steers the same.
 */
const KEY_DIRS: ReadonlyMap<string, SnakeDir> = new Map<string, SnakeDir>([
  ["ArrowUp", "up"],
  ["KeyW", "up"],
  ["ArrowDown", "down"],
  ["KeyS", "down"],
  ["ArrowLeft", "left"],
  ["KeyA", "left"],
  ["ArrowRight", "right"],
  ["KeyD", "right"],
]);

/** The arrow on a key — an SVG, so Android's text zoom cannot grow it. Points up; rotated. */
const ROTATE: Record<SnakeDir, number> = { up: 0, right: 90, down: 180, left: 270 };

function Arrow({ dir, size, look }: { dir: SnakeDir; size: number; look: SnakeLook }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ transform: `rotate(${ROTATE[dir]}deg)`, display: "block" }}
    >
      {look === "retro" ? (
        <path d="M12 5 L21 17 H3 Z" fill="currentColor" />
      ) : (
        <path
          d="M12 5.5 L19.5 14.5 H14.5 V19 H9.5 V14.5 H4.5 Z"
          fill="currentColor"
          strokeLinejoin="round"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      )}
    </svg>
  );
}

/** One key of the pad. `pointerdown`, never `click`: a turn lands the moment the thumb does. */
function PadKey({
  dir,
  rect,
  look,
  onTurn,
}: {
  dir: SnakeDir;
  rect: SnakeRect;
  look: SnakeLook;
  onTurn: (dir: SnakeDir) => void;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={fa.snake.pad[dir]}
      data-dir={dir}
      className={look === "retro" ? "snake-key snake-key--retro" : "snake-key"}
      style={{ position: "absolute", left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      onPointerDown={() => onTurn(dir)}
      onContextMenu={(event) => event.preventDefault()}
    >
      <Arrow dir={dir} size={Math.round(rect.w * 0.44)} look={look} />
    </button>
  );
}

const EXIT_BTN =
  "appearance-none m-0 cursor-pointer flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95";

export function SnakeTable({
  speed,
  look,
  scores,
  serverBest,
  onGameOver,
  mayStartAgain,
  onBack,
  onExit,
}: {
  speed: SnakeSpeed;
  look: SnakeLook;
  scores: SnakeBestScore;
  /** The server's latest answer for this speed's best — it may land after a run began (see below). */
  serverBest: number | null;
  /** The run ended: the container reads the gates' verdict for «دوباره». */
  onGameOver: () => void;
  /** «دوباره» asks the platform's gates first; false: the container shows the refusal instead. */
  mayStartAgain: () => boolean;
  /** «بازگشت»: Snake's start screen. */
  onBack: () => void;
  /** «خروج»: the lobby. */
  onExit: () => void;
}): ReactNode {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bonusRef = useRef<HTMLSpanElement>(null);
  const painterRef = useRef<SnakePainter | null>(null);
  const controllerRef = useRef<SnakeController | null>(null);

  const [layout, setLayout] = useState<SnakeLayout | null>(null);
  const [phase, setPhase] = useState<SnakePhase>("ready");
  const [count, setCount] = useState(3);
  const [score, setScore] = useState(0);
  const [end, setEnd] = useState<SnakeEnd | null>(null);
  /** This speed's best known when this run began — «رکورد جدید!» is measured against it. */
  const [bestAtStart, setBestAtStart] = useState(() => scores.best(speed));
  const [confirming, setConfirming] = useState(false);
  /** The phase, for code that must not re-run on it (the effects below, the controller's hooks). */
  const phaseRef = useRef<SnakePhase>("ready");
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;

  // ⚠️ THE SERVER'S BEST AT THIS SPEED CAN LAND AFTER THE RUN BEGAN — a «شروع» tapped before the start
  // screen's read answered. It is the best as it stood BEFORE this run, so it raises the run's own:
  // without it a phone that says 0 against a server that says 500 calls a 30 «رکورد جدید!». A
  // finished run keeps its own (the read after it may already hold its score).
  useEffect(() => {
    if (serverBest === null || phaseRef.current === "over") return;
    setBestAtStart((known) => Math.max(known, serverBest));
  }, [serverBest]);

  /** A running game whose score was kept on the way out — so the unmount does not post it twice. */
  const keptRef = useRef<SnakeGame | null>(null);
  /** Leaving a running game keeps its score (a finished one saved it when it ended). */
  const keep = (game: SnakeGame | null | undefined): void => {
    if (!game || game.over || keptRef.current === game) return;
    keptRef.current = game;
    scores.save(speed, game.score);
  };

  /** The value the chip shows (-1: none written yet) — it moves every 2nd step at most. */
  const shownBonusRef = useRef(-1);
  /** The bonus's value, written straight into its chip — a step never re-renders — and only when
   *  it changed. */
  const showBonus = (value: number): void => {
    const chip = bonusRef.current;
    if (!chip || value === shownBonusRef.current) return;
    shownBonusRef.current = value;
    chip.hidden = value <= 0;
    if (value > 0) chip.textContent = `★ ${faDigits(value)}`;
  };

  // THE BOX → THE LAYOUT. The box is everything under the header band, inside the safe-area insets.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const media = typeof window.matchMedia === "function";
    const fine = media && window.matchMedia("(pointer: fine)").matches;
    const measure = (): void => {
      const next = layoutSnake({
        width: box.clientWidth,
        height: box.clientHeight,
        grid: SNAKE_GRID,
        fine,
        dpr: window.devicePixelRatio || 1,
      });
      setLayout((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    // A resize of the box (a zoom resizes it too): the observer. A pixel ratio that changes with the
    // box left alone (the window moved to another screen): a query for the ratio in force, armed
    // again for the new one each time it stops matching. `?.`: an old WebView's list has only
    // `addListener`, and keeps the ratio it opened with.
    let ratio: MediaQueryList | null = null;
    const onRatio = (): void => {
      measure();
      watchRatio();
    };
    const watchRatio = (): void => {
      ratio?.removeEventListener?.("change", onRatio);
      ratio = media ? window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`) : null;
      ratio?.addEventListener?.("change", onRatio);
    };
    watchRatio();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(box);
    return () => {
      observer?.disconnect();
      ratio?.removeEventListener?.("change", onRatio);
    };
  }, []);

  // THE PAINTER: one per canvas; the whole board again on a resize (and at its first size).
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layout || layout.cell <= 0) return;
    if (!painterRef.current) {
      painterRef.current = new SnakePainter(canvas, SNAKE_GRID, SNAKE_BONUS_SIZE);
    }
    const painter = painterRef.current;
    painter.setLook(look);
    painter.resize(layout.px, layout.dpr);
    painter.full(controllerRef.current?.game ?? null);
  }, [layout, look]);

  // THE GAME: a controller for this screen's life, started at once, paused whenever the app is not
  // on screen. Created HERE, not in render: StrictMode mounts twice, and each mount gets its own.
  useEffect(() => {
    const { stepMs, points } = SNAKE_SPEEDS[speed];
    const controller = new SnakeController({
      clock: browserClock,
      stepMs,
      newGame: () =>
        new SnakeGame({
          grid: SNAKE_GRID,
          initialLength: SNAKE_INITIAL_LENGTH,
          growth: SNAKE_GROWTH,
          bonusGrowth: SNAKE_BONUS_GROWTH,
          points,
          bonusEvery: SNAKE_BONUS_EVERY,
          bonusSteps: SNAKE_BONUS_STEPS,
          bonusTick: SNAKE_BONUS_TICK,
          bonusSize: SNAKE_BONUS_SIZE,
        }),
      hooks: {
        onStart: (game) => {
          painterRef.current?.full(game);
          showBonus(0);
          setScore(0);
          setEnd(null);
          setBestAtStart(scores.best(speed));
        },
        onStep: (step, game) => {
          painterRef.current?.step(step, game);
          showBonus(step.bonusValue);
          if (step.scored > 0) setScore(step.score);
          if (step.over) {
            setEnd(step.over);
            scores.save(speed, step.score);
          }
        },
        onPhase: (next) => {
          phaseRef.current = next;
          setPhase(next);
          if (next === "over") onGameOverRef.current();
        },
        onCountdown: setCount,
      },
    });
    controllerRef.current = controller;

    // THE KEYBOARD (a desktop's): a key steers exactly as its pad key does — the same queue, so a
    // reversal is dropped and quick presses wait for their steps. Only in the 3-2-1 and the run:
    // paused (the «خروج» dialog too) or over, a key is left alone, not even prevented. A held key's
    // repeats are prevented but steer nothing, as the pad never repeats: a → held through a ↑ would
    // otherwise queue a → after the ↑.
    const onKey = (event: KeyboardEvent): void => {
      if (event.ctrlKey || event.altKey || event.metaKey) return; // the browser's shortcut
      const dir = KEY_DIRS.get(event.code);
      if (!dir || (controller.phase !== "countdown" && controller.phase !== "running")) return;
      event.preventDefault();
      if (event.repeat) return;
      controller.turn(dir);
    };
    window.addEventListener("keydown", onKey);

    // Hidden = the document is, or the app's fused activity signal says so (Telegram minimised the
    // Mini App, a pagehide, a freeze). A pause that beats the best saves it, in case the app is
    // never seen again.
    const away = (): boolean =>
      document.visibilityState === "hidden" || !lifecycle.store.getState().appActive;
    let hidden = false;
    const sync = (): void => {
      if (away() === hidden) return;
      hidden = !hidden;
      if (!hidden) {
        controller.resume("hidden");
        return;
      }
      controller.pause("hidden");
      const game = controller.game;
      if (game && !game.over && game.score > scores.best(speed)) scores.save(speed, game.score);
    };
    document.addEventListener("visibilitychange", sync);
    const offLifecycle = lifecycle.subscribe(sync);
    sync();
    controller.start();
    return () => {
      document.removeEventListener("visibilitychange", sync);
      offLifecycle();
      window.removeEventListener("keydown", onKey);
      // Anything else that takes this screen away from a running game (an accepted invite) keeps
      // its score too.
      keep(controller.game);
      controller.dispose();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
    // `scores` is the container's one instance for this visit; `speed` is fixed for the screen.
  }, [speed, scores]);

  const turn = (dir: SnakeDir): void => controllerRef.current?.turn(dir);

  const again = (): void => {
    if (!mayStartAgain()) return;
    controllerRef.current?.start();
  };

  const askExit = (): void => {
    if (phase === "over") {
      onExit();
      return;
    }
    controllerRef.current?.pause("dialog");
    setConfirming(true);
  };

  const best = Math.max(bestAtStart, score);
  const newRecord = phase === "over" && score > 0 && score > bestAtStart;
  /** The snake filled the board — the run ends as a WIN, on its own card: never the game over's. */
  const won = end === "full";
  const endTitle = won ? fa.snake.won : fa.snake.gameOver;
  const retro = look === "retro";

  return (
    <div
      style={{
        position: "relative",
        height: "var(--app-h)",
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(130% 120% at 50% 30%, var(--felt-2) 0%, var(--felt-edge) 55%, var(--boot-vignette) 100%)",
        color: "var(--text)",
        overflow: "hidden",
      }}
    >
      {/* THE BAND — exit · title (and the bonus's countdown) · the score box, the 40 px row every
          table has. `data-table-hud`: the game-over card starts below it, so «خروج» stays usable. */}
      <div
        data-table-hud
        className="absolute inset-x-0 top-0 z-10 flex items-start gap-2 px-4"
        style={{
          paddingTop: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <button
          type="button"
          onClick={askExit}
          aria-label={fa.snake.leave}
          data-testid="snake-exit"
          className={EXIT_BTN}
        >
          <GameIcon type="exit" size={20} />
        </button>
        <div className="flex h-10 min-w-0 flex-1 items-center justify-center gap-2">
          <span className="font-headline-md text-headline-md font-bold leading-tight text-secondary">
            {fa.snake.title}
          </span>
          <span
            ref={bonusRef}
            hidden
            aria-label={fa.snake.bonus}
            data-testid="snake-bonus"
            className="whitespace-nowrap rounded-full border border-solid border-secondary/50 bg-surface-container/80 px-2 py-0.5 font-body-md text-body-md font-bold tabular-nums text-secondary"
          />
        </div>
        <div className="ms-auto flex h-10 flex-none items-stretch overflow-hidden rounded-lg border border-outline-variant bg-surface-container/80 text-center shadow-sm">
          {(
            [
              [fa.snake.score, score, true],
              [fa.snake.record, best, false],
            ] as const
          ).map(([label, n, mine], i) => (
            <div
              key={label}
              data-testid={mine ? "snake-score" : "snake-best"}
              className={`box-content flex min-w-[40px] flex-none flex-col justify-center px-2 py-[3px] ${
                i === 1 ? "border-s border-outline-variant" : ""
              }`}
            >
              <span className="font-caption text-caption leading-none text-on-surface-variant">
                {label}
              </span>
              <span
                className={`font-body-lg text-body-lg font-bold leading-tight tabular-nums ${
                  mine ? "text-secondary" : "text-on-surface"
                }`}
              >
                {faDigits(n)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* THE BOX the layout is measured on: below the band, inside every inset. Left to right. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          paddingTop: `calc(${SNAKE_BAND_PX}px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))`,
          paddingBottom: "max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px))",
          paddingLeft: "max(env(safe-area-inset-left), var(--tg-safe-left, 0px))",
          paddingRight: "max(env(safe-area-inset-right), var(--tg-safe-right, 0px))",
        }}
      >
        <div ref={boxRef} dir="ltr" style={{ position: "relative", flex: 1, minWidth: 0 }}>
          {layout ? (
            <>
              <div
                className={retro ? "snake-frame snake-frame--retro" : "snake-frame"}
                role="img"
                aria-label={fa.snake.boardLabel}
                style={{
                  position: "absolute",
                  left: layout.board.x,
                  top: layout.board.y,
                  width: layout.board.w,
                  height: layout.board.h,
                }}
              >
                <canvas ref={canvasRef} style={{ display: "block" }} />
                {phase === "countdown" || (phase === "paused" && !confirming) ? (
                  <div
                    data-testid="snake-veil"
                    style={{
                      position: "absolute",
                      inset: layout.frame,
                      display: "grid",
                      placeItems: "center",
                      background: retro ? lcdColour("ground", 0.6) : "rgba(0, 0, 0, 0.35)",
                      color: retro ? lcdColour("pixel") : "rgb(var(--c-secondary))",
                      fontWeight: 800,
                      fontSize: phase === "countdown" ? Math.round(layout.board.w * 0.28) : 22,
                      lineHeight: 1,
                    }}
                  >
                    {phase === "countdown" ? faDigits(count) : fa.snake.paused}
                  </div>
                ) : null}
              </div>
              {DIRS.map((dir) => (
                <PadKey key={dir} dir={dir} rect={layout.buttons[dir]} look={look} onTurn={turn} />
              ))}
            </>
          ) : null}
        </div>
      </div>

      {phase === "over" ? (
        <EndCardShell
          label={endTitle}
          testId={won ? "snake-win" : "snake-end"}
          scrim="rgba(0, 0, 0, 0.45)"
          cardClassName={won || newRecord ? "end-card end-card--win" : "end-card"}
          cardStyle={{
            background: "var(--surface)",
            border: "1px solid var(--hairline)",
            borderRadius: "var(--radius-lg)",
            width: "100%",
            maxWidth: 320,
            textAlign: "center",
            boxShadow: "0 30px 70px rgba(0,0,0,.5)",
            gap: 16,
          }}
          actions={
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Button variant="primary" onClick={again}>
                {fa.snake.again}
              </Button>
              <Button variant="ghost" onClick={onBack}>
                {fa.snake.back}
              </Button>
            </div>
          }
        >
          <div style={{ fontSize: 40, lineHeight: 1.1 }} aria-hidden>
            {won || newRecord ? "🏆" : "🐍"}
          </div>
          <h2 style={{ margin: "4px 0 10px", fontSize: 20, fontWeight: 800 }}>{endTitle}</h2>
          <div style={{ display: "flex", justifyContent: "center", gap: 28 }}>
            <div data-testid="snake-end-score">
              <div style={{ fontSize: 13, color: "var(--hint)" }}>{fa.snake.score}</div>
              <div className="tabular-nums" style={{ fontSize: 30, fontWeight: 800 }}>
                {faDigits(score)}
              </div>
            </div>
            <div data-testid="snake-end-best">
              <div style={{ fontSize: 13, color: "var(--hint)" }}>{fa.snake.record}</div>
              <div className="tabular-nums" style={{ fontSize: 30, fontWeight: 800 }}>
                {faDigits(best)}
              </div>
            </div>
          </div>
          {newRecord ? (
            <div
              data-testid="snake-new-record"
              className="text-secondary"
              style={{ marginTop: 8, fontSize: 14, fontWeight: 700 }}
            >
              {fa.snake.newRecord}
            </div>
          ) : null}
        </EndCardShell>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          onConfirm={() => {
            setConfirming(false);
            keep(controllerRef.current?.game);
            onExit();
          }}
          onCancel={() => {
            setConfirming(false);
            controllerRef.current?.resume("dialog");
          }}
        />
      ) : null}
    </div>
  );
}
